import React, { createContext, useContext, useEffect, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useGQL } from './GQLClientProvider'
import {
  BasicEntities_TagFragmentFragment,
  BasicEntities_DepotFragmentFragment,
  BasicEntities_CarrierFragmentFragment,
  BasicEntities_CheckoutFragmentFragment,
  BasicEntities_CurrencyFragmentFragment,
  BasicEntities_PaymentFragmentFragment,
  BasicEntitiesQueryQuery,
} from '../gql/graphql'
import { graphql, useFragment } from '../gql'
import { GeoConfig } from 'depoto-core'
import { useCore, useLogin } from '../hooks'

// --- Fragment colocation: All fragments for basic entities are defined here for context-wide usage. ---
export const BasicEntities_TagFragment = graphql(`
  fragment BasicEntities_TagFragment on Tag {
    id
    name
    color
    externalId
    type
    ...TagComponent_TagFragment
  }
`)

export const BasicEntities_DepotFragment = graphql(`
  fragment BasicEntities_DepotFragment on Depot {
    id
    name
    company {
      id
    }
  }
`)

export const BasicEntities_CarrierFragment = graphql(`
  fragment BasicEntities_CarrierFragment on Carrier {
    id
    name
    color
  }
`)

export const BasicEntities_CheckoutFragment = graphql(`
  fragment BasicEntities_CheckoutFragment on Checkout {
    id
    name
  }
`)

export const BasicEntities_CurrencyFragment = graphql(`
  fragment BasicEntities_CurrencyFragment on Currency {
    id
    name
  }
`)

export const BasicEntities_PaymentFragment = graphql(`
  fragment BasicEntities_PaymentFragment on Payment {
    id
    name
  }
`)

// --- Query for all basic entities ---
const BasicEntitiesQuery = graphql(`
  query BasicEntitiesQuery {
    tags {
      items {
        ...BasicEntities_TagFragment
      }
    }
    depots {
      items {
        ...BasicEntities_DepotFragment
      }
    }
    carriers {
      items {
        ...BasicEntities_CarrierFragment
      }
    }
    checkouts {
      items {
        ...BasicEntities_CheckoutFragment
      }
    }
    currencies {
      items {
        ...BasicEntities_CurrencyFragment
      }
    }
    payments {
      items {
        ...BasicEntities_PaymentFragment
      }
    }
  }
`)

// NOTE: The context provides unmasked (unwrapped) types for all entities, using useFragment for type safety.
export type BasicEntitiesContextType = {
  tags: BasicEntities_TagFragmentFragment[]
  depots: BasicEntities_DepotFragmentFragment[]
  carriers: BasicEntities_CarrierFragmentFragment[]
  checkouts: BasicEntities_CheckoutFragmentFragment[]
  countries: any[]
  currencies: BasicEntities_CurrencyFragmentFragment[]
  payments: BasicEntities_PaymentFragmentFragment[]
}

export const BasicEntitiesContext = createContext<BasicEntitiesContextType>({
  tags: [],
  depots: [],
  carriers: [],
  checkouts: [],
  countries: [],
  currencies: [],
  payments: [],
})

export const BasicEntitiesProvider = ({ children }: { children: React.ReactNode }) => {
  const gql = useGQL()
  const { isLoggedIn } = useLogin()
  const { company } = useCore()
  const { data, refetch } = useQuery<BasicEntitiesQueryQuery>({
    queryKey: ['basicEntities'],
    queryFn: async () => await gql.request(BasicEntitiesQuery),
    enabled: isLoggedIn,
  })

  useEffect(() => {
    if (isLoggedIn) {
      refetch()
    }
  }, [isLoggedIn])

  // Countries are static/config-based
  const countries = GeoConfig?.countries || []
  const carrierRelations = (company?.carrierRelations || []).filter(c => c?.enable)
  const subCompaniesCarrierRelations = (company?.children || [])
    .map(c => c?.carrierRelations)
    .flat()
    .filter(c => c?.enable)
    .filter((c, index, self) => self?.findIndex(t => t?.carrier?.id === c?.carrier?.id) === index)

  const contextValue: BasicEntitiesContextType = useMemo(() => {
    return {
      tags: data?.tags?.items
        ? (useFragment(
            BasicEntities_TagFragment,
            data.tags.items.filter(Boolean),
          ) as BasicEntities_TagFragmentFragment[])
        : [],
      depots: data?.depots?.items
        ? (useFragment(
            BasicEntities_DepotFragment,
            data.depots.items.filter(Boolean),
          ) as BasicEntities_DepotFragmentFragment[])
        : [],
      carriers: data?.carriers?.items
        ? (
            useFragment(
              BasicEntities_CarrierFragment,
              data.carriers.items.filter(Boolean),
            ) as BasicEntities_CarrierFragmentFragment[]
          ).filter(c => [...carrierRelations, ...subCompaniesCarrierRelations].some(cr => cr.carrier.id === c.id))
        : [],
      checkouts: data?.checkouts?.items
        ? (useFragment(
            BasicEntities_CheckoutFragment,
            data.checkouts.items.filter(Boolean),
          ) as BasicEntities_CheckoutFragmentFragment[])
        : [],
      countries,
      currencies: data?.currencies?.items
        ? (useFragment(
            BasicEntities_CurrencyFragment,
            data.currencies.items.filter(Boolean),
          ) as BasicEntities_CurrencyFragmentFragment[])
        : [],
      payments: data?.payments?.items
        ? (useFragment(
            BasicEntities_PaymentFragment,
            data.payments.items.filter(Boolean),
          ) as BasicEntities_PaymentFragmentFragment[])
        : [],
    }
  }, [data, countries])

  return <BasicEntitiesContext.Provider value={contextValue}>{children}</BasicEntitiesContext.Provider>
}

export const useBasicEntities = () => {
  const context = useContext(BasicEntitiesContext)
  if (!context) {
    throw new Error('useBasicEntities must be used within a BasicEntitiesProvider')
  }
  return context
}
